// components/reports/ReportFilterBar.tsx
import React from 'react';
import {
  Stack, TextField, InputAdornment, Button, FormControl,
  InputLabel, Select, MenuItem, alpha,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

export interface FilterOption { value: string | number; label: string; }

export interface FilterField {
  type:        'date' | 'search' | 'select' | 'text';
  key:         string;
  label:       string;
  options?:    FilterOption[];
  placeholder?: string;
  minWidth?:   number;
}

interface ReportFilterBarProps {
  fields:      FilterField[];
  values:      Record<string, string>;
  onChange:    (key: string, val: string) => void;
  onClear:     () => void;
  accentColor: string;
}

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  fields, values, onChange, onClear, accentColor,
}) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
    {fields.map(f => {
      if (f.type === 'date') {
        return (
          <TextField key={f.key} size="small" label={f.label} type="date"
            value={values[f.key] || ''} onChange={e => onChange(f.key, e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: f.minWidth ?? 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        );
      }
      if (f.type === 'search' || f.type === 'text') {
        return (
          <TextField key={f.key} size="small" placeholder={f.placeholder ?? f.label}
            value={values[f.key] || ''} onChange={e => onChange(f.key, e.target.value)}
            InputProps={f.type === 'search' ? {
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            } : undefined}
            sx={{ minWidth: f.minWidth ?? 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        );
      }
      if (f.type === 'select') {
        return (
          <FormControl key={f.key} size="small" sx={{ minWidth: f.minWidth ?? 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
            <InputLabel>{f.label}</InputLabel>
            <Select value={values[f.key] || ''} label={f.label}
              onChange={e => onChange(f.key, e.target.value as string)}>
              <MenuItem value=""><em>All</em></MenuItem>
              {(f.options ?? []).map(o => (
                <MenuItem key={o.value} value={String(o.value)}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      return null;
    })}
    <Button size="small" variant="outlined" onClick={onClear}
      sx={{ borderRadius: 2, borderColor: alpha(accentColor, 0.4), color: accentColor }}>
      Clear
    </Button>
  </Stack>
);

export default ReportFilterBar;
